/**
 * SPEC-RedDotOps — client pure helpers (no React, no DOM).
 *
 * The Bad forms and the Admin card build their request bodies and labels here
 * so the rules are unit-tested (client/src/lib/__tests__/red-dot-helpers.test.ts)
 * and the components stay thin.
 */

import type { BadReasonUi } from "@/lib/agent-ui-helpers";
import {
  validateRedDotRoster,
  RED_DOT_MESSAGE_MAX,
  type RedDotRoster,
  type RedDotResult,
} from "@shared/red-dot";

export { RED_DOT_MESSAGE_MAX };
export type { RedDotRoster, RedDotResult };

// ── Agent Bad form, Step 2 ────────────────────────────────────────────────────

/** Step-2 chips, in display order. "slack" is the #ops chip (D1). */
export const AGENT_BAD_ACTIONS = ["phone", "text", "other", "slack"] as const;
export type AgentBadAction = typeof AGENT_BAD_ACTIONS[number];

export interface AgentBadFormState {
  action: AgentBadAction | null;
  otherText: string;
  note: string;
}

/** #ops makes the one text box required; every other chip keeps it optional. */
export function canConfirmAgentBad({ action, otherText, note }: AgentBadFormState): boolean {
  if (!action) return false;
  if (action === "other" && !otherText.trim()) return false;
  if (action === "slack" && !note.trim()) return false;
  return true;
}

export function agentBadNotePlaceholder(action: AgentBadAction | null): string {
  return action === "slack"
    ? "Message to #ops (required, ⌘V to paste a photo)…"
    : "Add a note (optional, ⌘V to paste a photo)…";
}

/**
 * POST /decision body for the agent Bad form. With #ops the same text goes out
 * as `redDotMessage` AND `initialNote`, so it is still saved as the note
 * exactly as today. A pasted photo rides on the note only — never to Slack.
 */
export function buildAgentBadBody(input: {
  reason: BadReasonUi;
  action: AgentBadAction;
  otherText: string;
  note: string;
  photoUrl: string | null;
}): Record<string, unknown> {
  const note = input.note.trim();
  return {
    decision: "BAD",
    badReason: input.reason,
    initialAction: input.action,
    ...(input.action === "other" ? { initialActionOther: input.otherText.trim() } : {}),
    ...(input.action === "slack" ? { redDotMessage: note } : {}),
    initialNote: note || undefined,
    ...(input.photoUrl ? { initialNotePhotoUrl: input.photoUrl } : {}),
  };
}

// ── Senior/Admin Bad form ─────────────────────────────────────────────────────

/** The #ops toggle → request field. The note (optional) doubles as the message server-side. */
export function seniorBadRedDotFields(slackOn: boolean): { sendRedDot: boolean } {
  return { sendRedDot: slackOn };
}

// ── Shared ────────────────────────────────────────────────────────────────────

/** D10 — "@Joseph @Tony"; null when nobody is switched on. */
export function mentionsLine(names: string[] | null | undefined): string | null {
  if (!names || names.length === 0) return null;
  return names.map((n) => `@${n}`).join(" ");
}

/** True only when a send was asked for and did not go out. */
export function redDotFailed(response: { redDot?: RedDotResult } | null | undefined): boolean {
  return !!response?.redDot?.requested && !response.redDot.ok;
}

/**
 * Plain-words text for the error codes the server sends back from /decision
 * and /red-dot. One mapping, shared by every red-dot toast and the Bad form.
 * Unknown codes fall through as-is so nothing is silently swallowed.
 */
const SERVER_ERROR_WORDS: Record<string, string> = {
  PRIOR_DAY_LOCKED: "This delivery is from an earlier day, so it can no longer be changed.",
  NOT_BAD: "This delivery is no longer marked Bad.",
  RED_DOT_MESSAGE_REQUIRED: "Type a message for #ops first.",
  "Agent must hold active claim to decide": "Your claim on this delivery has ended. Go back to the list and open it again.",
  read_only_impersonation: "You are viewing in read-only mode. Turn writes on to submit.",
  OUT_OF_QATT_SCOPE: "This task type is outside what your account can check.",
  FORBIDDEN: "Your account is not allowed to do this.",
  SLACK_NOT_CONFIGURED: "Slack is not set up on this server.",
  SLACK_TIMEOUT: "Slack took too long to answer.",
};

export function serverErrorWords(code: string | null | undefined): string {
  const key = (code ?? "").trim();
  if (!key) return "Something went wrong. Please try again.";
  return SERVER_ERROR_WORDS[key] ?? key;
}

/** §5.2 — standalone send (Retry, or a late "Send to Ops"). Throws with the server's error code. */
export async function postRedDot(deliveryId: string, message: string): Promise<RedDotResult> {
  const res = await fetch(`/api/qa/deliveries/${deliveryId}/red-dot`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ message: message.trim().slice(0, RED_DOT_MESSAGE_MAX) }),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new Error(body?.error || `red-dot ${res.status}`);
  return body?.redDot ?? { requested: true, ok: false };
}

// ── Admin roster card ─────────────────────────────────────────────────────────

export type RosterEdit = { ok: true; roster: RedDotRoster } | { ok: false; error: string };

/** Add a person, switched on. Validated with the same rules as the server. */
export function rosterAdd(roster: RedDotRoster, name: string, slackUserId: string): RosterEdit {
  return validateRedDotRoster({
    responders: [...roster.responders, { name, slackUserId, active: true }],
  });
}

export function rosterSetActive(roster: RedDotRoster, slackUserId: string, active: boolean): RedDotRoster {
  return {
    responders: roster.responders.map((r) => (r.slackUserId === slackUserId ? { ...r, active } : r)),
  };
}

export function rosterRemove(roster: RedDotRoster, slackUserId: string): RedDotRoster {
  return { responders: roster.responders.filter((r) => r.slackUserId !== slackUserId) };
}
